import { useModal } from "@ebay/nice-modal-react"
import { ArrowDown, ArrowUp, CopyPlus, Pencil, Plus, Trash2 } from "lucide-react"
import { observer } from "mobx-react"
import { useState } from "react"
import { ACTION_COLOR_STYLES } from "../../actions/actionStyles"
import { savedActionIcon } from "../../actions/actionIcons"
import type { SavedAction } from "../../actions/actionTypes"
import type { CodeStore } from "../../store/store"
import { ModalConfirm } from "../ui"
import { Switch } from "../ui/Switch"
import { ActionEditorModal } from "./ActionEditorModal"

function SectionLabel({ children, count }: { children: string; count?: number }) {
    return (
        <div className="mb-2 flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted">
            <span>{children}</span>
            {count !== undefined && <span className="bg-base-200 px-1.5 py-0.5">{count}</span>}
        </div>
    )
}

export const ActionsTab = observer(({ store }: { store: CodeStore }) => {
    const editor = useModal(ActionEditorModal)
    const confirm = useModal(ModalConfirm)
    const [operationError, setOperationError] = useState<string | null>(null)
    const actions = store.savedActions

    if (!actions) {
        return <div className="text-sm text-muted">Actions are still loading.</div>
    }

    const run = async (operation: () => Promise<void>) => {
        setOperationError(null)
        try {
            await operation()
        } catch (error) {
            setOperationError(error instanceof Error ? error.message : "The Action could not be updated")
        }
    }
    const remove = (action: SavedAction) => {
        confirm.show({
            title: "Remove Action",
            description: `Permanently remove the custom Action “${action.name}”?`,
            buttonText: "Remove",
            onConfirm: () => void run(() => actions.deleteAction(action.id)),
        })
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h3 className="text-base font-semibold text-base-content">Actions</h3>
                    <p className="mt-1 max-w-2xl text-sm text-muted">
                        One-click messages that enter the task queue in click order. Builtins update with WildLark; duplicate one to make it your own.
                    </p>
                </div>
                <button
                    type="button"
                    className="btn flex h-9 items-center gap-2 bg-primary px-3 text-sm font-medium text-primary-content hover:bg-primary/80"
                    onClick={() => editor.show({ actions })}
                >
                    <Plus size={14} />
                    New Action
                </button>
            </div>

            {(operationError || actions.error) && (
                <div className="border border-error/40 bg-error/10 p-3 text-sm text-error">{operationError ?? actions.error}</div>
            )}

            <section>
                <SectionLabel count={actions.installedActions.length}>Buttons</SectionLabel>
                {actions.installedActions.length === 0 ? (
                    <div className="border border-dashed border-border p-5 text-center text-sm text-muted">No Actions available.</div>
                ) : (
                    <div className="flex flex-col gap-1">
                        {actions.installedActions.map((action, index) => {
                            const Icon = savedActionIcon(action.icon)
                            return (
                                <div
                                    key={action.id}
                                    className="group grid grid-cols-[auto_minmax(0,1fr)] items-center gap-3 bg-base-200/50 p-3 hover:bg-base-200 min-[600px]:grid-cols-[auto_minmax(0,1fr)_auto]"
                                >
                                    <div
                                        className={`flex h-9 w-9 items-center justify-center ${ACTION_COLOR_STYLES[action.color].swatch} ${
                                            action.enabled ? "" : "opacity-40"
                                        }`}
                                    >
                                        <Icon size={15} />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="truncate text-sm font-medium text-base-content">{action.name}</span>
                                            {action.source && <span className="bg-primary/10 px-1.5 py-0.5 text-[10px] text-primary">Builtin</span>}
                                            {action.sessionLaunch && (
                                                <span className="bg-secondary px-1.5 py-0.5 text-[10px] text-secondary-content">Agent picker</span>
                                            )}
                                            {action.requiresConfirmation && <span className="bg-error/10 px-1.5 py-0.5 text-[10px] text-error">Confirm</span>}
                                            {!action.allowCombining && <span className="bg-base-300 px-1.5 py-0.5 text-[10px] text-muted">Standalone</span>}
                                            {!action.enabled && <span className="bg-base-300 px-1.5 py-0.5 text-[10px] text-muted">Off</span>}
                                        </div>
                                        <div data-action-prompt={action.id} className="mt-1 text-xs leading-5 text-muted [overflow-wrap:anywhere]">
                                            {action.prompt.split(/\r?\n[\t ]*\r?\n/).map((paragraph, paragraphIndex) => (
                                                <p
                                                    key={`${action.id}.prompt.${paragraphIndex}`}
                                                    data-action-prompt-paragraph="true"
                                                    className={paragraphIndex === 0 ? "whitespace-pre-wrap" : "mt-2 whitespace-pre-wrap"}
                                                >
                                                    {paragraph}
                                                </p>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="col-span-2 flex items-center justify-end gap-0.5 border-t border-border/60 pt-2 min-[600px]:col-span-1 min-[600px]:border-0 min-[600px]:pt-0">
                                        <Switch
                                            checked={action.enabled}
                                            onCheckedChange={(enabled) => void run(() => actions.setActionEnabled(action.id, enabled))}
                                            aria-label={`${action.enabled ? "Turn off" : "Turn on"} ${action.name}`}
                                            className={{ root: "mr-2" }}
                                        />
                                        <button
                                            type="button"
                                            className="btn flex h-9 w-9 items-center justify-center text-muted hover:bg-base-300 hover:text-base-content disabled:opacity-25"
                                            disabled={index === 0}
                                            onClick={() => void run(() => actions.moveAction(action.id, -1))}
                                            title="Move up"
                                            aria-label={`Move ${action.name} up`}
                                        >
                                            <ArrowUp size={13} />
                                        </button>
                                        <button
                                            type="button"
                                            className="btn flex h-9 w-9 items-center justify-center text-muted hover:bg-base-300 hover:text-base-content disabled:opacity-25"
                                            disabled={index === actions.installedActions.length - 1}
                                            onClick={() => void run(() => actions.moveAction(action.id, 1))}
                                            title="Move down"
                                            aria-label={`Move ${action.name} down`}
                                        >
                                            <ArrowDown size={13} />
                                        </button>
                                        <button
                                            type="button"
                                            className="btn flex h-9 w-9 items-center justify-center text-muted hover:bg-base-300 hover:text-base-content"
                                            onClick={() => editor.show({ actions, duplicateFromActionId: action.id })}
                                            title="Duplicate and customize"
                                            aria-label={`Duplicate ${action.name}`}
                                        >
                                            <CopyPlus size={13} />
                                        </button>
                                        {!action.source && (
                                            <>
                                                <button
                                                    type="button"
                                                    className="btn flex h-9 w-9 items-center justify-center text-muted hover:bg-base-300 hover:text-base-content"
                                                    onClick={() => editor.show({ actions, actionId: action.id })}
                                                    title="Edit"
                                                    aria-label={`Edit ${action.name}`}
                                                >
                                                    <Pencil size={13} />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn flex h-9 w-9 items-center justify-center text-muted hover:bg-error/10 hover:text-error"
                                                    onClick={() => remove(action)}
                                                    title="Remove"
                                                    aria-label={`Remove ${action.name}`}
                                                >
                                                    <Trash2 size={13} />
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                )}
            </section>
        </div>
    )
})
